import { makeStyles } from 'tss-react/mui';
import { accentSurface, accentSurfaceText } from '../../utils/accentSurface';
import { keyframes } from 'tss-react';

// Soft pulse around the avatar while a call is being set up or is ringing.
const pulse = keyframes({
    "0%": { boxShadow: "0 0 0 0 var(--ring-soft)" },
    "70%": { boxShadow: "0 0 0 14px rgba(0, 0, 0, 0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(0, 0, 0, 0)" },
});

const STATUS = {
    active: "#2e9e55",
    hold: "#8a8f98",
    calling: "#e8930c",
};

const useStyles = makeStyles()((theme) => ({
    // Fills the window under the app bar, so nothing hangs below the fold.
    paper: {
        width: "100%",
        height: "calc(var(--app-vh) - 56px)",
        [theme.breakpoints.up("sm")]: {
            height: "calc(var(--app-vh) - 64px)",
        },
        maxWidth: "100vw",
        display: "flex",
        flexDirection: "column",
    },
    toolbarRoot: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        width: "100%",
        height: "auto",
        minWidth: "360px",
        minHeight: "60px",
        borderRadius: "0 0 10px 10px",
        [theme.breakpoints.up("md")]: {
            minHeight: "90px",
        },
    },
    colorDefault: {
        ...accentSurface(theme),
    },
    title: {
        flex: "auto",
        color: accentSurfaceText(theme),
        fontWeight: 300,
        textAlign: "center",
        [theme.breakpoints.up("sm")]: {
            textAlign: "left",
        },
        // Room for the tab of the section menu at the left edge.
        [theme.breakpoints.between("sm", "md")]: {
            paddingLeft: theme.spacing(2),
        },
    },

    // Page area under the toolbar: a calm backdrop for the phone card.
    main: {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
        overflowY: "auto",
        background: theme.palette.background.default,
        "& .MuiTypography-body1": {
            color: theme.palette.text.primary,
        },
    },
    stage: {
        flex: 1,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: theme.spacing(3),
    },
    // The latest calls, beside the keypad while no call is in progress.
    recent: {
        width: "100%",
        maxWidth: 340,
        alignSelf: "center",
        display: "flex",
        flexDirection: "column",
        padding: theme.spacing(1.5, 1, 1),
        borderRadius: 20,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0px 10px 30px rgba(15, 35, 60, 0.10), 0px 1px 3px rgba(15, 35, 60, 0.08)",
    },
    recentHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(0.5, 1.5, 1),
    },
    recentTitle: {
        fontSize: 16,
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    recentAll: {
        padding: theme.spacing(0.25, 0.75),
        borderRadius: 6,
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: 500,
        color: theme.palette.primary.light,
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        },
    },
    recentItem: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        width: "100%",
        padding: theme.spacing(0.75, 1.5),
        borderRadius: 12,
        textAlign: "left",
        fontFamily: "inherit",
        transition: "background-color 0.15s ease",
        "&:hover, &:focus-visible": {
            backgroundColor: theme.palette.action.hover,
        },
    },
    recentWhen: {
        flexShrink: 0,
        fontSize: 12,
        color: theme.palette.text.secondary,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
    },
    recentEmpty: {
        padding: theme.spacing(3, 1.5),
        textAlign: "center",
        fontSize: 13.5,
        color: theme.palette.text.secondary,
    },
    card: {
        width: "100%",
        maxWidth: 380,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: theme.spacing(3.5, 3, 3),
        borderRadius: 20,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0px 10px 30px rgba(15, 35, 60, 0.10), 0px 1px 3px rgba(15, 35, 60, 0.08)",
    },
    cardDialer: {
        padding: theme.spacing(2, 3, 2.5),
    },
    modeHeader: {
        width: "100%",
        textAlign: "center",
        paddingBottom: theme.spacing(1),
    },
    modeTitle: {
        fontSize: 17,
        fontWeight: 500,
        lineHeight: 1.3,
        color: theme.palette.text.primary,
    },
    modeHint: {
        fontSize: 13,
        lineHeight: 1.4,
        color: theme.palette.text.secondary,
    },

    // ---- A call in the card
    avatarWrap: {
        display: "flex",
        justifyContent: "center",
        marginBottom: theme.spacing(2),
    },
    avatarGroup: {
        "& > *:not(:first-of-type)": {
            marginLeft: -18,
        },
    },
    centralAvatar: {
        width: 96,
        height: 96,
        fontSize: 34,
        fontWeight: 400,
        color: "#fff",
        backgroundColor: theme.palette.primary.dark,
        border: `3px solid ${theme.palette.background.paper}`,
        boxShadow: "0 0 0 3px var(--ring, transparent)",
        objectFit: "cover",
        borderRadius: "50%",
    },
    centralAvatarIcon: {
        width: 46,
        height: 46,
    },
    avatar: {
        width: 44,
        height: 44,
        fontSize: 17,
        color: "#fff",
        backgroundColor: theme.palette.primary.dark,
        boxShadow: "0 0 0 2px var(--ring, transparent)",
        border: `2px solid ${theme.palette.background.paper}`,
        objectFit: "cover",
        borderRadius: "50%",
    },
    avatarIcon: {
        width: 24,
        height: 24,
    },
    pulsing: {
        animation: `${pulse} 1.8s ease-out infinite`,
    },
    // Status colours, shared by rings, dots and pills through CSS variables.
    toneActive: {
        "--ring": STATUS.active,
        "--ring-soft": "rgba(46, 158, 85, 0.45)",
        "--tone": theme.palette.tone.success.main,
        "--tone-bg": theme.palette.tone.success.soft,
    },
    toneHold: {
        "--ring": STATUS.hold,
        "--ring-soft": "rgba(138, 143, 152, 0.45)",
        "--tone": theme.palette.tone.muted.main,
        "--tone-bg": theme.palette.tone.muted.soft,
    },
    toneCalling: {
        "--ring": STATUS.calling,
        "--ring-soft": "rgba(232, 147, 12, 0.45)",
        "--tone": theme.palette.tone.warning.main,
        "--tone-bg": theme.palette.tone.warning.soft,
    },
    toneIncoming: {
        "--ring": theme.palette.primary.light,
        "--ring-soft": "rgba(1, 186, 239, 0.45)",
        "--tone": theme.palette.tone.brand.main,
        "--tone-bg": theme.palette.tone.brand.soft,
    },
    name: {
        fontSize: 22,
        fontWeight: 500,
        lineHeight: 1.25,
        textAlign: "center",
        color: theme.palette.text.primary,
        wordBreak: "break-word",
    },
    number: {
        marginTop: 2,
        fontSize: 15,
        lineHeight: 1.4,
        textAlign: "center",
        color: theme.palette.text.secondary,
        fontVariantNumeric: "tabular-nums",
    },
    pill: {
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        marginTop: theme.spacing(1.5),
        padding: "4px 12px",
        borderRadius: 14,
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.4,
        color: "var(--tone)",
        backgroundColor: "var(--tone-bg)",
        fontVariantNumeric: "tabular-nums",
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: "50%",
        flexShrink: 0,
        backgroundColor: "var(--ring)",
    },
    pillExtra: {
        "&::before": {
            content: '"·"',
            marginRight: 7,
            opacity: 0.7,
        },
    },

    // ---- In-call controls: round buttons with a label, three per row
    controls: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 84px)",
        justifyContent: "center",
        rowGap: theme.spacing(2),
        columnGap: theme.spacing(1),
        marginTop: theme.spacing(3),
    },
    control: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
    },
    controlButton: {
        width: 52,
        height: 52,
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.action.hover,
        transition: "background-color 0.15s ease, color 0.15s ease",
        "&:hover": {
            backgroundColor: theme.palette.action.selected,
        },
        "&.Mui-disabled": {
            color: theme.palette.text.disabled,
            backgroundColor: theme.palette.action.hover,
            opacity: 0.6,
        },
    },
    controlButtonOn: {
        color: "#fff",
        backgroundColor: theme.palette.primary.light,
        "&:hover": {
            backgroundColor: theme.palette.primary.light,
            filter: "brightness(0.94)",
        },
    },
    controlButtonRecording: {
        color: "#fff",
        backgroundColor: "#e5322d",
        "&:hover": {
            backgroundColor: "#e5322d",
            filter: "brightness(0.94)",
        },
    },
    controlLabel: {
        fontSize: 12,
        lineHeight: 1.3,
        color: theme.palette.text.secondary,
        textAlign: "center",
    },
    controlLabelDisabled: {
        color: theme.palette.text.disabled,
    },

    // ---- Answer / hang up
    callButtons: {
        display: "flex",
        justifyContent: "center",
        gap: theme.spacing(5),
        marginTop: theme.spacing(3.5),
    },
    callButton: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
    },
    answer: {
        width: 60,
        height: 60,
        color: "#fff",
        backgroundColor: "#1faa4b",
        boxShadow: "0px 6px 14px rgba(31, 170, 75, 0.35)",
        "&:hover": {
            backgroundColor: "#1faa4b",
            filter: "brightness(0.94)",
        },
    },
    hangup: {
        width: 60,
        height: 60,
        color: "#fff",
        backgroundColor: "#e5322d",
        boxShadow: "0px 6px 14px rgba(229, 50, 45, 0.35)",
        "&:hover": {
            backgroundColor: "#e5322d",
            filter: "brightness(0.94)",
        },
    },

    // ---- Other calls, listed above the card
    ribbons: {
        width: "100%",
        maxWidth: 560,
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    ribbon: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 1.5, 1, 1.5),
        borderRadius: 14,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0px 4px 14px rgba(15, 35, 60, 0.08), 0px 1px 2px rgba(15, 35, 60, 0.06)",
    },
    ribbonInfo: {
        flex: 1,
        minWidth: 0,
    },
    ribbonName: {
        fontSize: 15,
        fontWeight: 500,
        lineHeight: 1.3,
        color: theme.palette.text.primary,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    ribbonMeta: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginTop: 1,
        fontSize: 12.5,
        lineHeight: 1.4,
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
    },
    ribbonActions: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
    },
    ribbonButton: {
        width: 38,
        height: 38,
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.action.hover,
        "&:hover": {
            backgroundColor: theme.palette.action.selected,
        },
    },
    ribbonAnswer: {
        width: 38,
        height: 38,
        color: "#fff",
        backgroundColor: "#1faa4b",
        "&:hover": {
            backgroundColor: "#1faa4b",
            filter: "brightness(0.94)",
        },
    },
    ribbonHangup: {
        width: 38,
        height: 38,
        marginLeft: theme.spacing(0.5),
        color: "#fff",
        backgroundColor: "#e5322d",
        "&:hover": {
            backgroundColor: "#e5322d",
            filter: "brightness(0.94)",
        },
    },
}));

export { useStyles }
